import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { currentObjective, getWindow, isError } from '../api';
import { ROUTES } from '../routes';
import {
  firstRun,
  gateIsClosed,
  readMark,
  writeMark,
  type FirstRun,
  type OnboardingMark,
  type SetupFacts,
} from './gate';

/**
 * One reading of where this learner is in their first run, shared by everything that asks. F-141.
 *
 * **Four screens ask the same question and they must not each answer it.** The gate redirects,
 * the welcome screen decides which of its two steps to render, the chat decides whether to show
 * the worked examples, and `Landing` decides where `/` goes. Four fetchers would be four answers
 * a poll apart — and because every one of those answers is a *navigation*, disagreement between
 * them is not a stale label, it is a learner bouncing between two addresses.
 *
 * So the facts are read once here, and `reread` is what a screen calls when it has just changed
 * one of them: stating an objective, and saving a reach window. Without that, `Objectives` would
 * create an objective and this context would go on believing there was none until a reload.
 *
 * The decision itself is in `gate.ts`, which has no React in it and is asserted from plain Node.
 * This file is the wiring, and wiring is the part no test in this package can see — the note
 * `session.ts` carries applies here word for word.
 */

interface FirstRunState {
  /** What the app owes this learner right now. `unknown` until the two reads land. */
  stage: FirstRun;
  /** The facts behind it, for screens that need more than the verdict. */
  facts: SetupFacts | undefined;
  /**
   * Both reads failed and stopped retrying. `Landing` turns this into a way out.
   *
   * Distinct from `facts === undefined`, which also covers "has not answered yet". A screen that
   * could not tell those apart would either offer a retry during an ordinary cold start or never
   * offer one at all — and the second of those is what the first version of this shipped with.
   */
  failed: boolean;
  /**
   * Hands the context a fact the caller has just been told authoritatively.
   *
   * **Because a screen that changed a fact must not ask an eventually consistent read to tell it
   * what it already knows.** `POST /api/objectives` answers with the objective's status and
   * `POST /api/window` answers with the stored window; both are stronger than the `gsi3` query and
   * the unconsistent `GetItem` that `reread` goes back to, and both are in hand in the same tick as
   * the navigation that depends on them.
   *
   * Without this the flagship path bounced: `reread` only *starts* a fetch, so `/welcome` mounted
   * against pre-create facts (`status: 'none'`), `firstRun` answered `open`, and the learner was
   * sent straight back to `/plan` — until a round trip later, or for ever if that one re-read
   * landed inside the index's consistency window.
   *
   * **Noted facts are sticky and win over a later read**, which is safe because both are monotone
   * within an account's session: an objective does not stop existing, and a saved reach window does
   * not become unsaved. A read that disagrees is stale by definition, and letting it win is the bug
   * this exists to close.
   */
  note: (patch: Partial<SetupFacts>) => void;
  /** Re-reads the objective and the window. For a screen that changed neither. */
  reread: () => void;
  /** Records how far they got, and re-decides immediately rather than on the next load. */
  mark: (mark: OnboardingMark) => void;
}

/**
 * How long to wait before each further attempt at the two reads.
 *
 * **A blip must not decide three screens for the rest of a session.** The first version had no
 * retry and deps of `[signedIn, asked]`, so one dropped request at cold start left `Landing` on
 * *"One moment…"* indefinitely, the gate silently inert and the worked examples never offered, with
 * nothing on screen saying anything had gone wrong.
 *
 * Two further attempts and then a stated failure, rather than a loop: somebody whose connection is
 * gone is better served by a button than by a screen that retries for ever and never says why.
 */
const RETRY_MS = [1_500, 4_000] as const;

const FirstRunContext = createContext<FirstRunState | null>(null);

/**
 * The two reads, and why they are these two and not `/api/progress`.
 *
 * `useSetupState` in `App.tsx` carried this note until this provider replaced it, and it still
 * holds: progress answers `null` until an objective is running — *"a plan awaiting approval is not a plan at nought per cent"* —
 * which is exactly the stage every caller here is asking about. The objective's status and
 * whether a window has been saved are the two facts, and they come from the two endpoints that
 * own them.
 */
export function FirstRunProvider({
  signedIn,
  userId,
  children,
}: {
  signedIn: boolean;
  /**
   * Whose first run this is, because the mark lives in this browser and browsers are shared.
   *
   * Keyed storage rather than one key: learner A walking the gate on a shared laptop used to leave
   * a `configuring` mark that learner B inherited — two example bubbles above a real thread, for
   * somebody this feature's own prose promises four times will never see them.
   *
   * May legitimately be absent: `/api/auth/me` answers `signedIn` without a `userId` for an account
   * whose row could not be read. Such a learner gets no mark and therefore no tour, which is the
   * right direction to be wrong in.
   */
  userId?: string | undefined;
  children: React.ReactNode;
}) {
  const [facts, setFacts] = useState<SetupFacts>();
  /**
   * What a screen told us directly, layered over the reads.
   *
   * Sticky, and it wins — see `note`. Reset only when the learner changes, because these two facts
   * are monotone within one account's session and are not across accounts.
   */
  const [noted, setNoted] = useState<Partial<SetupFacts>>({});
  const [failed, setFailed] = useState(false);
  const [mark, setMarkState] = useState<OnboardingMark | undefined>(() => readMark(userId));
  /* Bumped by `reread`, and the effect's only other dependency. A counter rather than a promise
     so two screens asking at once cost one round trip and not two competing ones. */
  const [asked, setAsked] = useState(0);

  /*
   * A different learner is a different first run.
   *
   * Signing out and in as somebody else does not remount this provider, so without this the second
   * learner would inherit the first one's noted facts and mark — the same bug the keyed storage
   * closes, arriving through React state instead of through `localStorage`.
   */
  useEffect(() => {
    setNoted({});
    setMarkState(readMark(userId));
  }, [userId]);

  useEffect(() => {
    if (!signedIn) {
      // Signed out there is no learner to be in a first run. Cleared rather than left standing,
      // so signing in as somebody else cannot inherit the previous account's stage.
      setFacts(undefined);
      setFailed(false);
      return;
    }
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const retry = (n: number) => {
      const wait = RETRY_MS[n];
      if (wait === undefined) {
        // Out of attempts. Said out loud rather than left as a screen that never resolves.
        setFailed(true);
        return;
      }
      timer = setTimeout(() => attempt(n + 1), wait);
    };

    /*
     * A failed read is not a state, and it is also not a resting place.
     *
     * Leaving the facts absent keeps `firstRun` on `unknown`, which redirects nobody — the safe
     * direction for both halves. An error read as "no window" would gate a learner who is perfectly
     * well configured out of their own plan over a dropped request; an error read as "configured"
     * would let the sequence F-140 apologised for happen again. So neither is inferred — but the
     * request is made again, because *safe* and *stuck* are not the same thing and the first
     * version of this was the second one.
     *
     * `.catch` as well as `isError`: `get` in `api.ts` answers an `ApiError` for a failed request,
     * but a throw from anywhere else on the path would otherwise be an unhandled rejection and
     * would take the retry down with it.
     */
    const attempt = (n: number) => {
      Promise.all([currentObjective(), getWindow()])
        .then(([objective, window]) => {
          if (!alive) return;
          if (isError(objective) || isError(window)) {
            retry(n);
            return;
          }
          setFailed(false);
          setFacts({
            status: objective.objective?.status ?? 'none',
            windowConfigured: window.configured,
          });
        })
        .catch(() => {
          if (alive) retry(n);
        });
    };

    setFailed(false);
    attempt(0);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [signedIn, asked]);

  const reread = useCallback(() => setAsked((n) => n + 1), []);

  const note = useCallback(
    (patch: Partial<SetupFacts>) => setNoted((current) => ({ ...current, ...patch })),
    [],
  );

  const remember = useCallback(
    (next: OnboardingMark) => {
      writeMark(next, userId);
      setMarkState(next);
    },
    [userId],
  );

  const value = useMemo<FirstRunState>(() => {
    /*
     * Reads first, noted facts over the top, the mark last.
     *
     * That order is the fix for the bounce: a stale `status: 'none'` from the objectives index
     * cannot overwrite the status the create route has just returned.
     */
    const known = facts ? { ...facts, ...noted, mark } : undefined;
    return { stage: firstRun(known), facts: known, failed, note, reread, mark: remember };
  }, [facts, noted, failed, mark, note, reread, remember]);

  return <FirstRunContext.Provider value={value}>{children}</FirstRunContext.Provider>;
}

/**
 * What the app owes this learner, for any screen inside the provider.
 *
 * Answers a stage of `unknown` rather than throwing when there is no provider, unlike
 * `useI18n`. A missing provider here is a screen that renders normally; a missing provider
 * that threw would be a white page, and the thing being decided is whether to show two
 * example bubbles.
 */
export function useFirstRun(): FirstRunState {
  return (
    useContext(FirstRunContext) ?? {
      stage: 'unknown',
      facts: undefined,
      failed: false,
      note: () => undefined,
      reread: () => undefined,
      mark: () => undefined,
    }
  );
}

/**
 * The gate: while a learner has an objective and nowhere to deliver it, this is the only screen.
 *
 * Leo was asked whether configuring the reach window should be mandatory and chose **no plan
 * screen until configured**. This is that decision, and it is deliberately a redirect rather than
 * a disabled button or a banner: a nag over a working product is a thing a learner learns to read
 * past, which is what `SetupNotice` had become. *"When they're done we know they're reachable"* is
 * only true if there is no way round.
 *
 * **Deep links included, which is the whole point of putting it here.** Every guarded route is
 * wrapped in this, so `/plan`, `/progress`, `/chat`, `/settings`, `/profile` and anything added
 * later are all covered by construction — a notification tap, a bookmark, a restored tab and a
 * typed address all land on `/welcome` while the gate is closed. The alternative, a check inside
 * each screen, is a list somebody has to remember to add to.
 *
 * What it does **not** cover, deliberately: `/welcome` itself (which would be an infinite
 * redirect — `gateIsClosed` exists so that is one comparison and not two), the unsubscribe
 * landing and the console, which are mounted outside the guard for their own reasons, and `/`,
 * which resolves through `Landing` — that one lands on `/plan` and is moved from there, which is
 * two redirects and the right screen.
 *
 * **Nor does it cover the shell**, and that is what keeps a gate from being a trap. `Shell` is
 * outside this, so the menu, the language switch and — the one that matters — **sign out** are
 * reachable throughout. A learner who cannot leave an app that is insisting on something is a
 * complaint of a completely different order from a learner who has to name a time of day.
 *
 * The accepted cost is that the menu goes on offering four sections while this is closed, and
 * each of them lands back here. Two things were weighed against fixing it: the state lasts the
 * few minutes between describing an objective and naming a reach window, once per account; and
 * suppressing the sections would put a second copy of this decision inside a component whose
 * layout contract (`--bar-h`, the docked rail, F-136) nothing in this package can test. A menu
 * that bounces is untidy. A rail that renders differently on a rule nobody asserts is the class
 * of fault this repository keeps a list of.
 */
export function FirstRunGate({ children }: { children: React.ReactNode }) {
  const { stage } = useFirstRun();
  const location = useLocation();

  /*
   * `unknown` renders the screen rather than waiting.
   *
   * The facts arrive a round trip after the app does, and a learner who is *not* in a first run
   * — which is everybody, after the first few minutes of their account — would otherwise watch
   * every screen in the app flash a blank frame on every cold start, for a gate that will not
   * apply to them. Somebody the gate does apply to gets the screen they asked for and is moved a
   * moment later, which is one visible transition on one occasion in the life of an account.
   */
  if (!gateIsClosed(stage)) return <>{children}</>;
  if (location.pathname === ROUTES.welcome) return <>{children}</>;
  return <Navigate to={ROUTES.welcome} replace />;
}
